/**
 * Usage Analytics Page
 * 
 * Comprehensive token usage tracking dashboard with:
 * - Summary cards (total tokens, credits, requests)
 * - Per-model breakdown table
 * - Time series chart (bar chart using CSS)
 * - Recent usage records
 */
import React, { useState, useEffect, useCallback } from 'react';
import { usageApi } from '../../services/cloudApi';
import { formatErrorMessage } from '../../utils/errorUtils';
import './Usage.css';

const PERIOD_OPTIONS = [
  { value: 'daily', label: 'Daily (30 days)' },
  { value: 'monthly', label: 'Monthly (12 months)' },
];

const PROVIDER_LABELS = {
  bedrock: 'AWS Bedrock',
  anthropic: 'Anthropic',
  openai: 'OpenAI',
  gemini: 'Google Gemini',
  ollama: 'Ollama',
};

function formatNumber(n) {
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + 'M';
  if (n >= 1_000) return (n / 1_000).toFixed(1) + 'K';
  return n.toLocaleString();
}

function formatCredits(credits) {
  const n = Number(credits) || 0;
  if (n === 0) return '0 cr';
  if (n < 0.01) return '<0.01 cr';
  if (n < 10) return n.toFixed(2) + ' cr';
  return n.toLocaleString(undefined, { maximumFractionDigits: 2 }) + ' cr';
}

function formatModelName(modelId) {
  if (!modelId) return 'Unknown';
  // Strip long bedrock prefixes for display
  return modelId
    .replace('eu.anthropic.', '')
    .replace('global.anthropic.', '')
    .replace(/-v\d+:\d+$/, '');
}

export function UsagePage() {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [period, setPeriod] = useState('monthly');
  const [providerFilter, setProviderFilter] = useState('');

  const loadAnalytics = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await usageApi.getAnalytics({
        period,
        provider: providerFilter || undefined,
      });
      setAnalytics(data);
    } catch (err) {
      console.error('Failed to load analytics:', err);
      setError(err.message);
    }
    setLoading(false);
  }, [period, providerFilter]);

  useEffect(() => {
    loadAnalytics();
  }, [loadAnalytics]);

  if (loading && !analytics) {
    return (
      <div className="usage-page">
        <div className="usage-loading">
          <div className="spinner" />
          <p>Loading usage analytics...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="usage-page">
        <div className="usage-error">
          <p>Failed to load analytics: {formatErrorMessage(error)}</p>
          <button onClick={loadAnalytics}>Retry</button>
        </div>
      </div>
    );
  }

  if (!analytics) return null;

  const totalTokens = analytics.total_input_tokens + analytics.total_output_tokens;
  const maxPeriodTokens = analytics.by_period.length > 0
    ? Math.max(...analytics.by_period.map(p => p.input_tokens + p.output_tokens), 1)
    : 1;

  // Hide non-LLM "publish" records (project publish actions) from the
  // Recent Activity list until we decide how to present them.
  const recentLlmRecords = analytics.recent_records.filter(
    r => r.agent_name !== 'publish'
  );

  return (
    <div className="usage-page">
      <div className="usage-header">
        <h1>Usage Analytics</h1>
        <div className="usage-filters">
          <select value={period} onChange={e => setPeriod(e.target.value)}>
            {PERIOD_OPTIONS.map(o => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          <select value={providerFilter} onChange={e => setProviderFilter(e.target.value)}>
            <option value="">All Providers</option>
            <option value="bedrock">AWS Bedrock</option>
            <option value="anthropic">Anthropic</option>
            <option value="openai">OpenAI</option>
            <option value="gemini">Google Gemini</option>
          </select>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="usage-cards">
        <div className="usage-card">
          <div className="card-label">Total Tokens</div>
          <div className="card-value">{formatNumber(totalTokens)}</div>
          <div className="card-detail">
            {formatNumber(analytics.total_input_tokens)} in / {formatNumber(analytics.total_output_tokens)} out
          </div>
        </div>
        <div className="usage-card">
          <div className="card-label">Credits used</div>
          <div className="card-value">{formatCredits(analytics.total_credits)}</div>
          <div className="card-detail">{analytics.total_requests} requests</div>
        </div>
        <div className="usage-card">
          <div className="card-label">Cache Tokens</div>
          <div className="card-value">{formatNumber(analytics.total_cache_read_tokens + analytics.total_cache_write_tokens)}</div>
          <div className="card-detail">
            {formatNumber(analytics.total_cache_read_tokens)} read / {formatNumber(analytics.total_cache_write_tokens)} write
          </div>
        </div>
        <div className="usage-card">
          <div className="card-label">Models Used</div>
          <div className="card-value">{analytics.by_model.length}</div>
          <div className="card-detail">across {new Set(analytics.by_model.map(m => m.provider)).size} providers</div>
        </div>
      </div>

      {/* Time Series Chart */}
      {analytics.by_period.length > 0 && (
        <div className="usage-section">
          <h2>Usage Over Time</h2>
          <div className="usage-chart">
            {analytics.by_period.map((p, i) => {
              const tokens = p.input_tokens + p.output_tokens;
              const height = (tokens / maxPeriodTokens) * 100;
              const label = period === 'daily'
                ? new Date(p.period).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                : new Date(p.period).toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
              return (
                <div key={i} className="chart-bar-wrapper" title={`${formatNumber(tokens)} tokens\n${formatCredits(p.credits)}`}>
                  <div className="chart-bar" style={{ height: `${Math.max(2, height)}%` }}>
                    <div className="chart-bar-input" style={{ height: `${tokens > 0 ? (p.input_tokens / tokens) * 100 : 50}%` }} />
                    <div className="chart-bar-output" />
                  </div>
                  <div className="chart-label">{label}</div>
                </div>
              );
            })}
          </div>
          <div className="chart-legend">
            <span><span className="legend-dot input" />Input</span>
            <span><span className="legend-dot output" />Output</span>
          </div>
        </div>
      )}

      {/* Per-Model Breakdown */}
      {analytics.by_model.length > 0 && (
        <div className="usage-section">
          <h2>By Model</h2>
          <div className="usage-table-wrapper">
            <table className="usage-table">
              <thead>
                <tr>
                  <th>Model</th>
                  <th>Provider</th>
                  <th>Input Tokens</th>
                  <th>Output Tokens</th>
                  <th>Requests</th>
                  <th>Credits</th>
                </tr>
              </thead>
              <tbody>
                {analytics.by_model.map((m, i) => (
                  <tr key={i}>
                    <td className="model-name">{formatModelName(m.model_id)}</td>
                    <td><span className={`provider-badge ${m.provider}`}>{PROVIDER_LABELS[m.provider] || m.provider}</span></td>
                    <td>{formatNumber(m.input_tokens)}</td>
                    <td>{formatNumber(m.output_tokens)}</td>
                    <td>{m.requests}</td>
                    <td className="cost">{formatCredits(m.credits)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Recent Records */}
      {recentLlmRecords.length > 0 && (
        <div className="usage-section">
          <h2>Recent Activity</h2>
          <div className="usage-table-wrapper">
            <table className="usage-table recent">
              <thead>
                <tr>
                  <th>Time</th>
                  <th>Agent</th>
                  <th>Model</th>
                  <th>Tokens</th>
                  <th>Credits</th>
                </tr>
              </thead>
              <tbody>
                {recentLlmRecords.map((r, i) => (
                  <tr key={i}>
                    <td className="time">{new Date(r.recorded_at).toLocaleString()}</td>
                    <td>{r.agent_name || '—'}</td>
                    <td className="model-name">{formatModelName(r.model_id)}</td>
                    <td>{formatNumber(r.input_tokens + r.output_tokens)}</td>
                    <td className="cost">{r.credits != null ? formatCredits(r.credits) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Empty state */}
      {analytics.total_requests === 0 && (
        <div className="usage-empty">
          <p>No usage data yet. Run a workflow to start tracking tokens!</p>
        </div>
      )}
    </div>
  );
}

export default UsagePage;


